import {useMemo} from 'react';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import {Box, Button, IconButton, Link, Tooltip} from '@mui/material';
import {isAxiosError} from 'axios';
import {MRT_ColumnDef} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {useGetCategories, useGetSubCategories} from '@entities/category';
import {
  Participant,
  useDeleteParticipant,
  useGetParticipants,
} from '@entities/participant';
import {getErrorMessageFromApiError} from '@shared/api';
import {
  MrTable,
  openConfirmation,
  TableRowActionsContainer,
} from '@shared/components';
import {
  CategoryFilter,
  FilterRow,
  SearchFilter,
  SubCategoryFilter,
} from '@shared/components/filters';
import {
  entityTableCommonProps,
  ROUTE__PARTICIPANT_CREATE,
  ROUTE__PARTICIPANT_DETAILS,
} from '@shared/constants';
import {useUrlState} from '@shared/hooks';

import {PaginationState} from '@/shared/types';

const localStorageKey = 'participantsTable';

type UrlState = PaginationState & {
  categoryId: string | null;
  subCategoryId: string | null;
  searchQuery?: string;
};

export const ParticipantsPage = () => {
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const {mutateAsync: deleteParticipant, isPending: isDeleting} =
    useDeleteParticipant();
  const {urlState, setUrl} = useUrlState<UrlState>({
    pageIndex: 0,
    pageSize: 20,
    categoryId: null,
    subCategoryId: null,
    searchQuery: undefined,
  });

  const {data, isPending, error} = useGetParticipants({
    page: urlState.pageIndex + 1,
    pageSize: urlState.pageSize,
    categorySlug: urlState.categoryId ?? undefined,
    subcategorySlug: urlState.subCategoryId ?? undefined,
    searchQuery: urlState.searchQuery,
  });

  const {data: categories} = useGetCategories();
  const {data: subCategories} = useGetSubCategories(urlState.categoryId!, {
    enabled: !!urlState.categoryId,
  });

  const categoryNameBySlug = useMemo(() => {
    const map: Record<string, string> = {};
    for (const cat of categories ?? []) {
      map[cat.id] = cat.name;
    }
    return map;
  }, [categories]);

  const subCategoryNameBySlug = useMemo(() => {
    const map: Record<string, string> = {};
    for (const sc of subCategories ?? []) {
      map[sc.id] = sc.name;
    }
    return map;
  }, [subCategories]);

  const columns = useMemo<MRT_ColumnDef<Participant>[]>(
    () => [
      {
        accessorKey: 'names',
        header: 'Name',
        size: 220,
        enableSorting: false,
        Cell: ({row}) => {
          const {participantId, names} = row.original;
          return (
            <Link
              component={RouterLink}
              to={ROUTE__PARTICIPANT_DETAILS.replace(
                ':participant_id',
                participantId,
              )}
            >
              {names[0] ?? participantId}
            </Link>
          );
        },
      },
      {
        accessorKey: 'abbreviation',
        header: 'Abbreviation',
        size: 120,
        enableSorting: false,
      },
      {
        accessorKey: 'nation',
        header: 'Nation',
        size: 100,
        enableSorting: false,
      },
      {
        accessorKey: 'categorySlug',
        header: 'Category',
        size: 180,
        enableSorting: false,
        Cell: ({row}) =>
          categoryNameBySlug[row.original.categorySlug] ??
          row.original.categorySlug,
      },
      {
        accessorKey: 'subcategorySlug',
        header: 'Subcategory',
        size: 180,
        enableSorting: false,
        Cell: ({row}) => {
          const slug = row.original.subcategorySlug;
          if (!slug) return '—';
          return subCategoryNameBySlug[slug] ?? slug;
        },
      },
    ],
    [categoryNameBySlug, subCategoryNameBySlug],
  );

  const handleDelete = async (participant: Participant) => {
    try {
      await openConfirmation({
        title: 'Delete participant',
        content: `Delete ${participant.names[0] ?? participant.participantId} (${participant.participantId})?`,
        okButtonText: 'Delete',
      });
    } catch {
      return;
    }

    try {
      await deleteParticipant(participant.participantId);
      enqueueSnackbar('Participant deleted successfully', {
        variant: 'success',
      });
      if (data?.items.length === 1 && urlState.pageIndex > 0) {
        setUrl(prev => ({...prev, pageIndex: prev.pageIndex - 1}));
      }
    } catch (deleteError) {
      const errorMessage = isAxiosError(deleteError)
        ? getErrorMessageFromApiError(deleteError)
        : '';
      enqueueSnackbar(`Could not delete participant. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  return (
    <MrTable
      error={error}
      columns={columns}
      data={data?.items ?? []}
      renderRowActions={({row}) => (
        <TableRowActionsContainer>
          <Tooltip title="Delete participant">
            <span>
              <IconButton
                size="small"
                aria-label={`Delete participant ${row.original.names[0] ?? row.original.participantId}`}
                disabled={isDeleting}
                onClick={() => void handleDelete(row.original)}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </TableRowActionsContainer>
      )}
      renderTopToolbarCustomActions={() => {
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            gap={2}
          >
            <Button onClick={() => navigate(ROUTE__PARTICIPANT_CREATE)}>
              Create participant
            </Button>
            <FilterRow>
              <SearchFilter
                value={urlState.searchQuery}
                onChange={searchQuery =>
                  setUrl(prev => ({...prev, pageIndex: 0, searchQuery}))
                }
              />

              <CategoryFilter
                size="small"
                value={urlState.categoryId}
                sx={{minWidth: 200}}
                onChange={categoryId =>
                  setUrl(prev => ({
                    ...prev,
                    pageIndex: 0,
                    categoryId,
                    subCategoryId: null,
                  }))
                }
              />

              <SubCategoryFilter
                size="small"
                categoryId={urlState.categoryId}
                value={urlState.subCategoryId}
                sx={{minWidth: 200}}
                onChange={subCategoryId =>
                  setUrl(prev => ({
                    ...prev,
                    pageIndex: 0,
                    subCategoryId,
                  }))
                }
              />
            </FilterRow>
          </Box>
        );
      }}
      localStorageKeyForSettings={localStorageKey}
      enableHiding
      enablePagination
      manualPagination
      onPaginationChange={updater =>
        setUrl(prev => {
          const nextPagination =
            typeof updater === 'function' ? updater(prev) : updater;
          return {...prev, ...nextPagination};
        })
      }
      rowCount={data?.total ?? 0}
      state={{
        isLoading: isPending,
        pagination: {
          pageIndex: urlState.pageIndex,
          pageSize: urlState.pageSize,
        },
      }}
      {...entityTableCommonProps}
      enableRowActions
    />
  );
};
